import { createAsyncAction, createStandardAction, AppThunk } from 'typesafe-actions';

import { getDictionary } from '@travel/i18n';
import { getTranslation } from '@travel/translation';
import { isNotEmptyArray, isNotEmptyValue } from '@travel/utils';

import { updateSEOData } from 'store/seo/actions';

import paths from 'constants/paths';
import { seoLabelTypes, DESCRIPTION, PAGE_INDICATOR, SUBDIVISION, TITLE } from 'constants/seo';
import { HOTEL_LIST_SORT_VALUES } from 'constants/sort';
import { HotelList, HotelListErrors, RequestBody } from 'HotelList-Types';
import { isInSortCategory } from 'store/utils';
import { isDatedUrl, normalizePathIdentifier } from 'utils/search';
import { fetchProviderList } from './api';
import { getHotelList } from './selectors';

import { getUserIdentifier } from 'store/common/selectors';

export const fetchHotelListAsync = createAsyncAction(
  'FETCH_HOTEL_LIST_REQUEST',
  'FETCH_HOTEL_LIST_SUCCESS',
  'FETCH_HOTEL_LIST_FAILURE',
)<undefined, HotelList, HotelListErrors>();

export const fetchMoreHotelListAsync = createAsyncAction(
  'FETCH_MORE_HOTEL_LIST_REQUEST',
  'FETCH_MORE_HOTEL_LIST_SUCCESS',
  'FETCH_MORE_HOTEL_LIST_FAILURE',
)<undefined, HotelList, HotelListErrors>();
export const fetchHotelListFilterAsync = createAsyncAction(
  'FETCH_HOTEL_LIST_FILTER_REQUEST',
  'FETCH_HOTEL_LIST_FILTER_SUCCESS',
  'FETCH_HOTEL_LIST_FILTER_FAILURE',
)<undefined, HotelList, undefined>();

export const fetchHotelList = (query: RequestBody): AppThunk => async (
  dispatch,
  getState,
  { apiClient },
) => {
  dispatch(fetchHotelListAsync.request());
  const userIdentifier = getUserIdentifier(getState());

  try {
    const clonedQuery = { ...query };
    if (!isInSortCategory(HOTEL_LIST_SORT_VALUES, clonedQuery.sort)) {
      clonedQuery.sort = HOTEL_LIST_SORT_VALUES.Provider_Recommend;
    }
    const response = await fetchProviderList(apiClient, {
      ...clonedQuery,
      rzCookie: userIdentifier,
    });
    dispatch(fetchHotelListAsync.success(response));
  } catch (error) {
    dispatch(fetchHotelListAsync.failure(error as HotelListErrors));
    throw error;
  }
};

export const fetchMoreHotels = (params: RequestBody): AppThunk => async (
  dispatch,
  getState,
  { apiClient },
) => {
  dispatch(fetchMoreHotelListAsync.request());

  const state = getState();
  const { offset, limit } = state.hotelList.hotels;
  const userIdentifier = getUserIdentifier(state);

  const clonedParams = { ...params };
  if (!isInSortCategory(HOTEL_LIST_SORT_VALUES, clonedParams.sort)) {
    clonedParams.sort = HOTEL_LIST_SORT_VALUES.Provider_Recommend;
  }

  const req = {
    ...clonedParams,
    offset: limit + offset,
    limit: limit,
    rzCookie: userIdentifier,
  };

  try {
    const response = await fetchProviderList(apiClient, req);
    dispatch(fetchMoreHotelListAsync.success(response));
  } catch (error) {
    dispatch(fetchMoreHotelListAsync.failure(error as HotelListErrors));
  }
};

export const fetchHotelListFilter = (query: RequestBody): AppThunk => async (
  dispatch,
  getState,
  { apiClient },
) => {
  dispatch(fetchHotelListFilterAsync.request());

  try {
    const clonedQuery = { ...query };
    if (!isInSortCategory(HOTEL_LIST_SORT_VALUES, clonedQuery.sort)) {
      clonedQuery.sort = HOTEL_LIST_SORT_VALUES.Provider_Recommend;
    }
    // limit could be 1 since we will not use providers data here
    const response = await fetchProviderList(apiClient, {
      ...clonedQuery,
      offset: 0,
      limit: 1,
    });

    dispatch(fetchHotelListFilterAsync.success(response));
  } catch (error) {
    const prevState = getState().hotelList?.hotels;
    if (prevState) {
      dispatch(fetchHotelListFilterAsync.success(prevState));
    } else {
      dispatch(fetchHotelListFilterAsync.failure());
    }
  }
};

export const fetchSEOData = (pageNumber: number): AppThunk => async (dispatch, getState) => {
  const state = getState();
  const { hotels } = getHotelList(state);
  const dictionary = getDictionary(state);
  const isPrefectureOrMultiAreaSearch = hotels.accommodations?.length !== 1;
  const mediaUrl = isNotEmptyArray(hotels?.providers) && hotels?.providers[0]?.media[0]?.url;
  const applicableName = isPrefectureOrMultiAreaSearch
    ? hotels.accommodationArea?.name
    : hotels.accommodations[0]?.name;
  const canonicalUrl = isPrefectureOrMultiAreaSearch
    ? hotels.accommodationArea?.pathId
    : hotels.accommodations[0]?.pathId;

  const category = isPrefectureOrMultiAreaSearch
    ? seoLabelTypes[SUBDIVISION]
    : seoLabelTypes[hotels.accommodations[0].category];

  const params = state.router.location.location?.search;
  const isDynamicUrl = isNotEmptyValue(params);
  const isDatedParameter = isDynamicUrl ? isDatedUrl(params) : false;

  const titleId = isDynamicUrl
    ? 'Domestic.JRDP.Hotel_List.Subdivision.Title'
    : `Domestic.JRDP.Hotel_List.${category}.${TITLE}`;

  const descriptionId = isDynamicUrl
    ? 'Domestic.JRDP.Hotel_List.Generic.Description'
    : `Domestic.JRDP.Hotel_List.${category}.${DESCRIPTION}`;

  let pathUrl = `${paths.providerList.pathResolver(canonicalUrl)}`;
  if (pageNumber !== 1) {
    const { normalizePath } = normalizePathIdentifier(pathUrl);
    pathUrl = `${normalizePath}${PAGE_INDICATOR}${pageNumber}/`;
  }

  dispatch(
    updateSEOData({
      title: canonicalUrl
        ? getTranslation(
            {
              id: titleId,
              data: { place: applicableName },
            },
            dictionary,
          )?.join('')
        : getTranslation(
            { id: 'DP_Common.Error_Message.Application_Error.Fatal.HTML_Title' },
            dictionary,
          )?.join(''),
      description: getTranslation(
        {
          id: descriptionId,
          data: { place: applicableName },
        },
        dictionary,
      )?.join(''),
      isIndexablePage: isDatedParameter ? false : hotels.isSeoIndexExpected,
      canonicalPath: pathUrl,
      imageUrl: mediaUrl,
      isDynamicUrl,
      isDatedTypeThreeUrl: isDatedParameter,
    }),
  );
};

export const updatePendingFilters = createStandardAction('UPDATE_HOTEL_LIST_PENDING_FILTERS')<
  HotelList
>();

export const setIsFirstTimeLoading = createStandardAction(
  'SET_IS_FIRST_TIME_HOTEL_LIST_LOADING',
).map((payload: boolean) => ({
  payload,
}));
